import { z } from "zod";
import { patientGroupSchema } from "./patient-group";
import { onboardingWaitingOnSchema } from "./onboarding-status";
import {
  journeyFunnelPeriodSchema,
  journeyFunnelStepKeySchema,
  journeyProgressSchema,
} from "./onboarding-timeline";
import { bookingDropOffCountsSchema } from "./booking-diagnostics";

// TES-1076 — the onboarding funnel in admin: from the first F-01 answers to a
// booked first care meeting, as Emelie listed the steps. TES-1092 adds three
// in-app steps around the booking start.
//
// Every number is an aggregate count. Nothing in the report names, numbers or
// otherwise identifies a patient; test patients (`isTest`) are excluded.

/** Emelie's ten steps in her order, with TES-1092's three in-app steps. */
export const ONBOARDING_FUNNEL_STEPS = [
  // 1. "Frågorna inskickade" — web lead or in-app F-01.
  "questions_submitted",
  // 2. "BankID påbörjat"
  "bankid_started",
  // 3. "BankID genomfört" — a new patient row.
  "bankid_completed",
  // 4. "E-post tillagd / verifierad" — counted as verified; added rides along.
  "email",
  // 5. "Personlig återkoppling visad" — /start rendered her F-01 feedback.
  "feedback_shown",
  // 6. "Appöverlämning visad" — the app hand-off was rendered.
  "app_handoff_shown",
  // 7. "Appknapp klickad" — a store button followed via /api/go/*.
  "app_button_clicked",
  // TES-1105 — "Appen öppnad första gången": an install's first open,
  // reported by the app before any sign-in (app 2.0.5), counted once per
  // person. TES-1202: only an install linked to a patient counts; one
  // nobody signed in on (store review, pre-launch robots) is only in the
  // `app_not_identified` diagnostic beside the step.
  "app_first_open",
  // TES-1105 — "Inloggning i appen med BankID": her first completed BankID
  // started in the app (`bankid_sessions.startMethod = app`).
  "app_bankid_login",
  // 8. "Första inloggning i appen"
  "app_first_login",
  // TES-1092 — where an eligible patient stops between the app and a booking.
  // "F-01-resultat visat i appen" — the app fetched her F-01 result.
  "f01_result_shown",
  // "Bokningsvyn öppnad" — the app fetched the free slots.
  "booking_slots_opened",
  // 9. "Bokning påbörjad" — the price screen opened (the app creates the
  // first-consult payment row there) or she chose "Boka vårdmöte".
  "booking_started",
  // TES-1092 — "Betalningsfönstret öppnat": she tapped pay and accepted the
  // terms, right before the payment sheet opens.
  "payment_sheet_opened",
  // 10. "Bokning slutförd" — a first care meeting booked.
  "booking_completed",
] as const;
export type OnboardingFunnelStepKey = (typeof ONBOARDING_FUNNEL_STEPS)[number];

/**
 * TES-1092 — the steps an API from before TES-1092 does not send. Their daily
 * counts are optional in the schema so a page newer than its API still
 * parses; such an API simply has no row for them in `steps`.
 */
export const ONBOARDING_FUNNEL_LATER_STEPS = [
  "app_first_open",
  "app_bankid_login",
  "f01_result_shown",
  "booking_slots_opened",
  "payment_sheet_opened",
] as const satisfies readonly OnboardingFunnelStepKey[];
type LaterStep = (typeof ONBOARDING_FUNNEL_LATER_STEPS)[number];

/**
 * Sub-counts shown beside a step's main number.
 *
 * - `web` / `app`: where step 1 happened (web lead vs in-app F-01).
 * - `web_not_identified`: the part of `web` whose lead was never linked to a
 *   patient (she did not complete BankID on the web).
 * - `email_added`: step 4's "tillagd"; the step's own count is "verifierad".
 * - `app_not_identified` (TES-1105): installs first opened in the period
 *   that nobody has signed in on, counted per install. TES-1202: NOT part of
 *   `app_first_open` (an API before it included them) — beside it, since
 *   most are store review and pre-launch robots.
 */
export const ONBOARDING_FUNNEL_BREAKDOWN_KEYS = [
  "web",
  "web_not_identified",
  "app",
  "email_added",
  "app_not_identified",
] as const;
export type OnboardingFunnelBreakdownKey =
  (typeof ONBOARDING_FUNNEL_BREAKDOWN_KEYS)[number];

// ── Steps 5–7: first-party events the web records ─────────────────────────
//
// They had no server signal before TES-1076. The web reports them to the API
// server-side (no cookie, no third-party script — the site is cookieless,
// TES-930) and the API stores one DomainEvent per patient and Stockholm day.

export const ONBOARDING_WEB_STEPS = [
  // TES-1167: since the visible beacon below, this SSR event is only the
  // diagnostic "rendered": the server decided to render the result view,
  // which proves nothing about the browser.
  "feedback_shown",
  "app_handoff_shown",
  "app_button_clicked",
  // TES-1167 — "Personlig återkoppling visad" on the web: a beacon the
  // browser sends once the result view has rendered and is on screen.
  "feedback_visible",
] as const;
export type OnboardingWebStep = (typeof ONBOARDING_WEB_STEPS)[number];

/** The DomainEvent.eventKey each web step is stored under. */
export const ONBOARDING_EVENT_KEYS: Record<OnboardingWebStep, string> = {
  feedback_shown: "onboarding_feedback_shown",
  app_handoff_shown: "onboarding_app_handoff_shown",
  app_button_clicked: "onboarding_app_button_clicked",
  feedback_visible: "onboarding_feedback_visible",
};

// ── TES-1092: in-app steps the API records itself ─────────────────────────
//
// No app change: each is recorded inside the request the app already makes
// when it shows the step, one DomainEvent per patient, step and Stockholm
// day (source "app"). "Betalningsfönstret öppnat" needs no event — it is
// `consult_payments.termsConsentAt`, written when she taps pay.

export const ONBOARDING_APP_STEPS = [
  "f01_result_shown",
  "booking_slots_opened",
] as const;
export type OnboardingAppStep = (typeof ONBOARDING_APP_STEPS)[number];

/** The DomainEvent.eventKey each in-app step is stored under. */
export const ONBOARDING_APP_EVENT_KEYS: Record<OnboardingAppStep, string> = {
  f01_result_shown: "onboarding_f01_result_shown",
  booking_slots_opened: "onboarding_booking_slots_opened",
};

// ── TES-1105: the app's first open ────────────────────────────────────────
//
// The app mints a random install id (UUID v4) on its first launch — no
// device fingerprint, no advertising id — and reports it once, anonymously,
// to a public route. At her first BankID sign-in in the app it sends the
// same id with her session, and the server links the install to her.

export const APP_INSTALL_PLATFORMS = ["ios", "android"] as const;
export type AppInstallPlatform = (typeof APP_INSTALL_PLATFORMS)[number];

const installId = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  );

/** POST public-api/v1/app-installs/first-open — no session, always 204. */
export const appFirstOpenInputSchema = z
  .object({
    installId,
    platform: z.enum(APP_INSTALL_PLATFORMS),
    // The marketing version ("2.0.5"); absent when the build cannot say.
    appVersion: z
      .string()
      .regex(/^[0-9A-Za-z.+-]{1,32}$/)
      .optional(),
  })
  .strict();
export type AppFirstOpenInput = z.infer<typeof appFirstOpenInputSchema>;

/** POST patient-api/v1/me/app-install — her session; always 204. */
export const appInstallLinkInputSchema = appFirstOpenInputSchema;
export type AppInstallLinkInput = AppFirstOpenInput;

export const APP_STORES = ["app_store", "play"] as const;
export type AppStore = (typeof APP_STORES)[number];

/**
 * TES-1084 — where a store click came from (`src` on /api/go/*): `email` is
 * the app-download reminder's button (/api/go/app?src=email).
 *
 * TES-1096 — `web` is the site's own app hand-off (its badges and its desktop
 * QR code), `ig` a link placed on Instagram. Only this coarse tag is stored,
 * on the click's DomainEvent; an untagged click predates the tagging and was
 * made on the site, so the report reads it as `web`.
 */
export const APP_CLICK_SOURCES = ["web", "email", "ig"] as const;
export type AppClickSource = (typeof APP_CLICK_SOURCES)[number];

// ── TES-1167: origin channel, event platform, hand-off kind ──────────────
//
// Emelie 2026-10-08: the funnel is split by where the patient STARTED (her
// origin channel, one per patient, first touch) and every event says where
// IT happened (its platform). Both are coarse classes read from a
// User-Agent server-side or from the app's own platform; the User-Agent
// itself is never stored.

/** Where one event happened. `unknown`: no usable User-Agent. */
export const EVENT_PLATFORMS = [
  "web-desktop",
  "web-mobile",
  "ios",
  "android",
  "unknown",
] as const;
export type EventPlatform = (typeof EVENT_PLATFORMS)[number];

/**
 * A patient's original start channel (first touch). The four segments
 * Emelie asked for, plus honest buckets for a channel known without its
 * device (`web-unknown`, `app-unknown`) and for no derivable channel at
 * all (`unknown`, patients from before the start method was recorded).
 */
export const ORIGIN_CHANNELS = [
  "web-desktop",
  "web-mobile",
  "ios",
  "android",
  "web-unknown",
  "app-unknown",
  "unknown",
] as const;
export type OriginChannel = (typeof ORIGIN_CHANNELS)[number];

/** The four segments Emelie named; the rest are shown only when non-empty. */
export const PRIMARY_ORIGIN_CHANNELS = [
  "web-desktop",
  "web-mobile",
  "ios",
  "android",
] as const satisfies readonly OriginChannel[];

/**
 * Which fact decided the origin, stored beside it so the backfill's
 * coverage can be read back:
 *
 * - `lead_ua`: a web lead from before her patient row, its device read
 *   from the User-Agent on lead creation (TES-1167 onward);
 * - `lead_bankid_ua`: such a lead without a recorded device, the device
 *   taken from her first BankID on the web (TES-1088's device class);
 * - `lead_only`: such a lead, device unknown;
 * - `bankid_web_ua`: no earlier lead, her first BankID was started on the
 *   web (QR or same device), device from that attempt;
 * - `app_install` / `push_token`: she started in the app (BankID started
 *   in the app, or an in-app F-01 with no earlier lead), platform from her
 *   first linked install (app >= 2.0.5) or else her first push token;
 * - `app_no_platform`: she started in the app, platform unknown;
 * - `none`: nothing to derive it from.
 */
export const ORIGIN_SOURCES = [
  "lead_ua",
  "lead_bankid_ua",
  "lead_only",
  "bankid_web_ua",
  "app_install",
  "push_token",
  "app_no_platform",
  "none",
] as const;
export type OriginSource = (typeof ORIGIN_SOURCES)[number];

/**
 * Step 4's kinds ("Appöverlämningslänk, QR-kod eller appbutikslänk
 * använd"):
 *
 * - `qr`: the desktop QR code, scanned (its signed token, tag `web`);
 * - `handoff_link`: "Fortsätt i Ella-appen", tapped (beacon);
 * - `store_link`: an App Store / Google Play link via /api/go/*;
 * - `open_app`: "Öppna appen", tapped (beacon);
 * - `email_link`: the reminder e-mail's button or QR code (one link, so the
 *   two cannot be told apart).
 */
export const APP_HANDOFF_KINDS = [
  "qr",
  "handoff_link",
  "store_link",
  "open_app",
  "email_link",
] as const;
export type AppHandoffKind = (typeof APP_HANDOFF_KINDS)[number];

/**
 * TES-1167 — steps the app itself reports, from app 2.0.8. Until then the
 * API's own `f01_result_shown` (the result was FETCHED) stands in as a
 * labelled proxy.
 */
export const ONBOARDING_APP_CLIENT_STEPS = ["f01_result_visible"] as const;
export type OnboardingAppClientStep =
  (typeof ONBOARDING_APP_CLIENT_STEPS)[number];
export const ONBOARDING_APP_CLIENT_EVENT_KEYS: Record<
  OnboardingAppClientStep,
  string
> = {
  f01_result_visible: "onboarding_f01_result_visible",
};

/** Every step a client (the web's SSR, its beacons, the app) may report. */
export const ONBOARDING_CLIENT_STEPS = [
  ...ONBOARDING_WEB_STEPS,
  ...ONBOARDING_APP_CLIENT_STEPS,
] as const;
export type OnboardingClientStep = (typeof ONBOARDING_CLIENT_STEPS)[number];

export const onboardingEventInputSchema = z
  .object({
    step: z.enum(ONBOARDING_CLIENT_STEPS),
    // Only for app_button_clicked: which store button she followed.
    store: z.enum(APP_STORES).optional(),
    // Only for app_button_clicked: set when the click came from an e-mail.
    source: z.enum(APP_CLICK_SOURCES).optional(),
    // TES-1167 — only for app_button_clicked: which hand-off she used.
    kind: z.enum(APP_HANDOFF_KINDS).optional(),
  })
  .strict();
export type OnboardingEventInput = z.infer<typeof onboardingEventInputSchema>;

/**
 * TES-1092 — the reminder e-mail's click, forwarded by the web's /api/go/app.
 * `token` is the opaque signed token from the button's URL (it names the
 * patient; the caller cannot); `store` is the one the web redirected to.
 */
export const onboardingEmailClickInputSchema = z
  .object({
    token: z.string().min(1).max(200),
    store: z.enum(APP_STORES),
    // TES-1096 — the link's `src`. The same token now rides on the web's
    // desktop QR code (`web`) as well as the reminder e-mail; absent means
    // the e-mail, which is all a pre-TES-1096 web sends.
    source: z.enum(APP_CLICK_SOURCES).optional(),
  })
  .strict();
export type OnboardingEmailClickInput = z.infer<
  typeof onboardingEmailClickInputSchema
>;

// ── The admin report ──────────────────────────────────────────────────────

const count = z.number().int().nonnegative();

// TES-1084 — the app-download reminder's effect, shown under steps 7–8.
//
// - `scheduled` / `sent` / `cancelled`: `scheduled_notifications` rows of
//   type `app_download`, on the day each was created, sent or cancelled (a
//   push token cancels a pending one: the app was opened first).
// - `app_login_after_sent`: patients whose first app trace (step 8) came
//   within `loginWindowDays` after their reminder was sent, on the day of
//   that first trace.
// - `clicked_from_email`: store clicks through the reminder's button
//   (step 7 with source `email`), one per patient and day. TES-1092: the
//   button carries a signed token, so a click from a mail app counts too;
//   before that only a browser holding her web session could be attributed.
export const APP_DOWNLOAD_REMINDER_KEYS = [
  "scheduled",
  "sent",
  "cancelled",
  "app_login_after_sent",
  "clicked_from_email",
] as const;
export type AppDownloadReminderKey =
  (typeof APP_DOWNLOAD_REMINDER_KEYS)[number];

/** How long after a sent reminder a first app login still counts for it. */
export const APP_DOWNLOAD_REMINDER_LOGIN_WINDOW_DAYS = 7;

const reminderCounts = z.object(
  Object.fromEntries(
    APP_DOWNLOAD_REMINDER_KEYS.map((k) => [k, count]),
  ) as Record<AppDownloadReminderKey, typeof count>,
);

/**
 * TES-1096 — the reminders SCHEDULED in the period, each followed to where it
 * is now. The activity counts above put a row on the day it was created,
 * sent or cancelled, so "sent" can exceed "scheduled": a reminder fires a day
 * after it is scheduled, and one scheduled the day before the period is sent
 * inside it (`sentFromEarlier`). These follow the same people instead.
 *
 * - `sent` / `cancelled` / `pending`: their status now (`pending` includes a
 *   row being sent; a failed or skipped row is in none of the three).
 * - `appOpenedAfterSent`: her first app trace after the reminder was sent,
 *   within `loginWindowDays`, with no app trace before it.
 * - `alreadyInAppWhenSent`: she had used the app before the reminder went
 *   out (opened it without allowing notifications, so nothing cancelled it).
 * - `clicked`: she clicked the reminder's button at least once.
 */
export const appDownloadReminderCohortSchema = z.object({
  scheduled: count,
  sent: count,
  cancelled: count,
  pending: count,
  appOpenedAfterSent: count,
  alreadyInAppWhenSent: count,
  clicked: count,
  sentFromEarlier: count,
});
export type AppDownloadReminderCohort = z.infer<
  typeof appDownloadReminderCohortSchema
>;

// ── TES-1096: when each measurement started ───────────────────────────────
//
// A step recorded by an event only exists from the deploy that added it, so a
// zero before that is "not measured", not drop-off. Each date is the
// Stockholm day of the first row of that kind (any patient, test patients
// included — the first row is when the code went live), or null when none
// exists yet. Read from the data rather than a constant, so it is right on
// every box whatever day each one was deployed.
export const MEASURED_SIGNALS = [
  // Steps 5–7, the web's events (TES-1076).
  "feedback_shown",
  "app_handoff_shown",
  "app_button_clicked",
  // The in-app events (TES-1092).
  "f01_result_shown",
  "booking_slots_opened",
  // The pay tap (`consult_payments.termsConsentAt`, TES-433).
  "payment_sheet_opened",
  // A BankID sign-in in the app, by start method (TES-1088): an app trace
  // that needs no notification permission.
  "app_bankid",
  // A reminder click counted from a mail app, by its token (TES-1092).
  "email_click_token",
  // A click tagged `web` or `ig` (TES-1096).
  "click_source_tag",
  // TES-1105 — the app's anonymous first-open report (app 2.0.5).
  "app_first_open",
  // TES-1109 — the e-mail steps' audit rows (CAR-112), so the cohort's two
  // e-mail steps carry a "Mäts sedan" like the app steps after them.
  "email_captured",
  "email_verified",
  // TES-1167 — the web's visible beacon, the app's on-screen event (app
  // 2.0.8), and the platform recorded on an event.
  "feedback_visible",
  "f01_result_visible",
  "event_platform",
] as const;
export type MeasuredSignal = (typeof MEASURED_SIGNALS)[number];

export const measurementStartSchema = z.object({
  key: z.enum(MEASURED_SIGNALS),
  /** Stockholm day, YYYY-MM-DD, or null when nothing is recorded yet. */
  since: z.string().nullable(),
});
export type MeasurementStart = z.infer<typeof measurementStartSchema>;

/**
 * TES-1096 — patient BankID, by person and by attempt. Only a completed
 * attempt carries an identity, so a person can be counted only once she
 * completes: `personsStarted` is the new patients with an attempt in the
 * period, `personsCompleted` the new patient rows. `attempts` and
 * `attemptsNeverCompleted` (an attempt with no identity, returning logins'
 * failures included) are diagnostics and never a funnel population.
 */
export const bankIdPersonsSchema = z.object({
  personsStarted: count,
  personsCompleted: count,
  attempts: count,
  attemptsNeverCompleted: count,
});
export type BankIdPersons = z.infer<typeof bankIdPersonsSchema>;

// TES-1088 — patient BankID attempts by how they were started and on what
// kind of device. `unrecorded` is an attempt from before the recording.
// - `qr`: on the web, shown as a QR code and never switched;
// - `autostart`: on the web on a phone, BankID opened by itself (TES-1622);
// - `same_device`: on the web, the patient tapped "open BankID on this
//   device";
// - `app`: started in the mobile app.
export const BANKID_START_METHOD_KEYS = [
  "qr",
  "autostart",
  "same_device",
  "app",
  "unrecorded",
] as const;
export type BankIdStartMethodKey = (typeof BANKID_START_METHOD_KEYS)[number];
export const BANKID_DEVICE_CLASS_KEYS = [
  "mobile",
  "desktop",
  "unknown",
] as const;
export type BankIdDeviceClassKey = (typeof BANKID_DEVICE_CLASS_KEYS)[number];

export const onboardingFunnelReportSchema = z.object({
  /** Inclusive Europe/Stockholm days, YYYY-MM-DD. */
  from: z.string(),
  to: z.string(),
  timeZone: z.literal("Europe/Stockholm"),
  /**
   * One row per step, in funnel order. A person counts on the day she FIRST
   * reached the step, so the range total counts each person once — except
   * where the source carries no identity (anonymous web leads, BankID
   * attempts that never completed), which count per attempt.
   */
  steps: z.array(
    z.object({
      key: z.enum(ONBOARDING_FUNNEL_STEPS),
      count,
      breakdown: z.array(
        z.object({ key: z.enum(ONBOARDING_FUNNEL_BREAKDOWN_KEYS), count }),
      ),
    }),
  ),
  /** Every day of the range, oldest first, with each step's count. */
  daily: z.array(
    z.object({
      day: z.string(),
      counts: z.object(
        Object.fromEntries(
          ONBOARDING_FUNNEL_STEPS.map((k) => [
            k,
            (ONBOARDING_FUNNEL_LATER_STEPS as readonly string[]).includes(k)
              ? count.optional()
              : count,
          ]),
        ) as Record<Exclude<OnboardingFunnelStepKey, LaterStep>, typeof count> &
          Record<LaterStep, z.ZodOptional<typeof count>>,
      ),
    }),
  ),
  /** TES-1084 — the app-download reminder, range totals and per day. */
  appDownloadReminder: z.object({
    loginWindowDays: z.number().int().positive(),
    totals: reminderCounts,
    daily: z.array(z.object({ day: z.string(), counts: reminderCounts })),
    /** TES-1096 — optional so a page newer than its API still parses. */
    scheduledInPeriod: appDownloadReminderCohortSchema.optional(),
  }),
  /** TES-1096 — optional so a page newer than its API still parses. */
  bankId: bankIdPersonsSchema.optional(),
  /** TES-1096 — optional so a page newer than its API still parses. */
  measuredSince: z.array(measurementStartSchema).optional(),
  /**
   * TES-1102 — why people who started booking in the period did not book,
   * as staff recorded it: one count per category, every category listed.
   * Counts only; no person and no note. Optional for an older API.
   */
  dropOffReasons: bookingDropOffCountsSchema.optional(),
  /**
   * TES-1088 — every patient BankID attempt in the range (returning logins
   * included, test patients out), one row per start method and device class
   * that occurred. Counted per attempt: an attempt carries no identity until
   * it completes. Optional so a page newer than its API still renders.
   */
  bankIdStartMethods: z
    .array(
      z.object({
        method: z.enum(BANKID_START_METHOD_KEYS),
        deviceClass: z.enum(BANKID_DEVICE_CLASS_KEYS),
        started: count,
        completed: count,
      }),
    )
    .optional(),
});
export type OnboardingFunnelReport = z.infer<
  typeof onboardingFunnelReportSchema
>;

// ── TES-1117 S3: the funnel per journey ───────────────────────────────────
//
// Emelie: "Counts and conversion are computed within the same journey." With
// the cohort filtered to one journey (new treatment or transfer), the
// report also carries that journey's own funnel (JOURNEY_FUNNEL_STEPS in
// onboarding-timeline.ts). A patient is in exactly one journey:
// resolvePatientGroups, the same reader as the patient list's "Resa", so
// someone without an F-01 answer counts as Ny behandling.
//
// ITS OWN PERIOD (Ö-354). The funnel counts the journey's people whose start
// falls in the chosen period (7/30/90 days back from today, or all; default
// 30 days), not the cohort table's from–to. The transfer journey runs for
// weeks, so each step counts what she has reached by the time the report is
// read, with no follow-up window. Each step also splits the people standing
// AT it into ongoing and dropped out (journeyProgress); a finished journey
// is neither. Counts only.
const journeyProgressCounts = {
  /** Still in an ongoing journey: never a drop-out. */
  ongoing: count,
  droppedOut: count,
};
export const journeyFunnelSchema = z.object({
  journey: patientGroupSchema,
  period: journeyFunnelPeriodSchema,
  /** The journey's people who started in the period. */
  size: count,
  /** All of them, by journeyProgress; the rest are finished. */
  ...journeyProgressCounts,
  /** Reached by now, in the journey's step order. */
  steps: z.array(
    z.object({
      key: journeyFunnelStepKeySchema,
      count,
      /** Of the people standing at this step (isAtJourneyFunnelStep). */
      ...journeyProgressCounts,
    }),
  ),
});
export type JourneyFunnel = z.infer<typeof journeyFunnelSchema>;

// The drill-down from one journey-funnel step: the people standing AT it
// (isAtJourneyFunnelStep), from the same period and positions as the counts.
// Admin only and identified, exactly like the contact list below: the API
// logs a read of every patient it returns. A row carries her name and her
// onboarding status (S1), never a form answer.
export const journeyStepPatientSchema = z.object({
  patientId: z.string(),
  /** Decrypted name, or "Namn saknas". */
  patientName: z.string(),
  /** Her start (ISO). */
  startedAt: z.string(),
  /** S1's precise status label. */
  status: z.string(),
  waitingOn: onboardingWaitingOnSchema.nullable(),
  /** Her latest onboarding activity (ISO), or null. */
  latestActivityAt: z.string().nullable(),
  /** Ö-354: ongoing, dropped out or finished. */
  progress: journeyProgressSchema,
});
export type JourneyStepPatient = z.infer<typeof journeyStepPatientSchema>;

export const journeyStepPatientsSchema = z.object({
  period: journeyFunnelPeriodSchema,
  journey: patientGroupSchema,
  step: journeyFunnelStepKeySchema,
  /** Latest activity first. */
  rows: z.array(journeyStepPatientSchema),
});
export type JourneyStepPatients = z.infer<typeof journeyStepPatientsSchema>;

// ── TES-1096: cohort conversion ("Kohortkonvertering") ────────────────────
//
// The report above counts what HAPPENED in the period, step by step, so its
// step-to-step ratios divide two different groups of people. The cohort
// follows the same people instead.
//
// WHO IS IN IT. Every real patient (test patients out) whose first
// measurable step falls in the period. Her start is the earliest of: a web
// lead linked to her, her first completed BankID (her patient row), and an
// F-01 submitted in the app. An anonymous lead (never identified) is not a
// person and is only counted on the side (`unlinkedLeads`).
//
// START CHANNEL. `web` when a lead is linked to her from before her patient
// row, or her first BankID was started on the web (QR or same device);
// otherwise `app` (F-01 or BankID in the app first).
//
// THE WINDOW. Each person is followed for `windowDays` from her own start: a
// step counts if she first reached it before her window closed, however
// late in the period she started. A person whose window is still open is
// in `openWindows`, and the cohort is `complete` only once every window has
// closed — until then its later steps can still grow.
export const COHORT_WINDOW_DAYS = 7;

export const COHORT_CHANNELS = ["web", "app"] as const;
export type CohortChannel = (typeof COHORT_CHANNELS)[number];

/** Emelie's cohort steps, in her order (TES-1093 point 1). */
export const COHORT_STEPS = [
  // "slutförde BankID" — her patient row.
  "bankid_completed",
  // TES-1109 — "E-post tillagd": she gave an address (her first
  // `patient.email_captured` audit row), verified or not.
  "email_added",
  // "E-post verifierad" — her first `patient.email_verified` audit row.
  "email_verified",
  // "såg den personliga återkopplingen" — web step 5.
  "feedback_shown",
  // "nådde appöverlämningen" — web step 6.
  "app_handoff_shown",
  // TES-1105 — the three app-transition steps, in Emelie's order
  // (2026-10-05). They replace TES-1096's single "öppnade appen" (her first
  // app trace of any kind), which mixed the three together.
  // "Klickat på länken till App Store/Google Play" — any store click.
  "app_button_clicked",
  // "Öppnat appen första gången" — the first open of an install that was
  // later linked to her; the time is the install's first open.
  "app_first_open",
  // "Loggat in i appen med BankID" — a completed BankID started in the app.
  "app_bankid_login",
  // "påbörjade en bokning"
  "booking_started",
  // TES-1092 — she tapped pay; the payment sheet opens next.
  "payment_sheet_opened",
  // "slutförde och betalade bokningen" — a first care meeting is created
  // only once Stripe confirms the payment.
  "booking_completed",
] as const;
export type CohortStepKey = (typeof COHORT_STEPS)[number];

/**
 * Breakdowns of each channel's cohort. Every key is computed when the report
 * is read; nothing but the click's coarse tag is stored for it.
 *
 * - `entry_source`: the linked lead's UTM tags. `ig` for Instagram (a
 *   source of ig/instagram, or an instagram.com referrer), `campaign` for any
 *   other utm_source, `none` without one; `app` for the app channel.
 * - `device`: the device class of her first completed BankID (TES-1088);
 *   `unknown` before that was recorded.
 * - `handoff`: the tag of her latest store click before she first opened
 *   the app — TES-1105: her install's first open when one is linked to her,
 *   else her first app trace — or before her window closed, if she has
 *   not: `web` (the
 *   site's buttons or its desktop QR code), `email` (the reminder's button
 *   or QR code), `ig`, or `none` when no click was recorded.
 */
export const COHORT_DIMENSIONS = ["entry_source", "device", "handoff"] as const;
export type CohortDimension = (typeof COHORT_DIMENSIONS)[number];
export const COHORT_ENTRY_SOURCES = ["ig", "campaign", "none", "app"] as const;
export type CohortEntrySource = (typeof COHORT_ENTRY_SOURCES)[number];
export const COHORT_DEVICES = ["mobile", "desktop", "unknown"] as const;
export type CohortDevice = (typeof COHORT_DEVICES)[number];
export const COHORT_HANDOFF_SOURCES = ["web", "email", "ig", "none"] as const;
export type CohortHandoffSource = (typeof COHORT_HANDOFF_SOURCES)[number];

const cohortStepCounts = z.array(
  z.object({ key: z.enum(COHORT_STEPS), count }),
);

const cohortGroup = z.object({
  /** People in this group. */
  size: count,
  /** People whose window has not closed yet. */
  openWindows: count,
  /** People who reached each step within their window, in step order. */
  steps: cohortStepCounts,
});

// ── TES-1167: the six-step funnel per origin segment ──────────────────────
//
// Emelie 2026-10-08. The cohort's people (same start, same window) split by
// their ORIGIN channel and followed through six steps, from her F-01 to her
// first BankID in the app:
//
// 1. "E-post verifierad" — her first `patient.email_verified` audit row.
// 2. "Personlig återkoppling genererad" — server-derived: a web lead linked
//    to her (her F-01 result; a minor's link is refused), or an F-01
//    submitted in the app.
// 3. "Personlig återkoppling visad" — the result finished loading and was
//    on screen. Web: the browser's visible beacon. App: the app's on-screen
//    event (app 2.0.8); until then the API's fetch-based `f01_result_shown`,
//    counted as a PROXY and reported apart (`visibleBy.app_fetch_proxy`).
//    The web's SSR event is only the diagnostic `webRendered`.
// 4. "Appöverlämningslänk, QR-kod eller appbutikslänk använd" — her first
//    hand-off click of any kind; `handoffKinds` splits it.
// 5. "Appen öppnad första gången" — the first open of an install linked to
//    her (app >= 2.0.5).
// 6. "Inloggad i appen med BankID" — her first completed BankID started in
//    the app.
//
// A step counts when she reached it within her window, in any order: a
// patient who started in the app opened it and signed in (5, 6) before she
// verified her e-mail (1). Step 4 does not apply to an app start (her result
// is shown in the app itself); it is still counted, and the page says so.
export const SEGMENT_FUNNEL_STEPS = [
  "email_verified",
  "feedback_generated",
  "feedback_visible",
  "app_handoff_used",
  "app_first_open",
  "app_bankid_login",
] as const;
export type SegmentFunnelStepKey = (typeof SEGMENT_FUNNEL_STEPS)[number];

export const segmentFunnelSchema = z.object({
  origin: z.enum(ORIGIN_CHANNELS),
  size: count,
  openWindows: count,
  steps: z.array(
    z.object({
      key: z.enum(SEGMENT_FUNNEL_STEPS),
      count,
      /**
       * Where the event that reached the step happened, for the steps an
       * event records (3, 4 and 5). Empty for the others.
       */
      platforms: z.array(
        z.object({ platform: z.enum(EVENT_PLATFORMS), count }),
      ),
    }),
  ),
  /** "E-post verifierad → Personlig återkoppling visad": of the verified. */
  verifiedToVisible: z.object({ verified: count, visible: count }),
  /** How step 3 was first reached. */
  visibleBy: z.object({
    web_visible: count,
    app_visible: count,
    app_fetch_proxy: count,
  }),
  /** Diagnostic: the web's SSR decided to render her result. */
  webRendered: count,
  /** Step 4 by the kind of her first hand-off click. */
  handoffKinds: z.array(z.object({ kind: z.enum(APP_HANDOFF_KINDS), count })),
});
export type SegmentFunnel = z.infer<typeof segmentFunnelSchema>;

// ── TES-1323: desired support by F-01 version ─────────────────────────────
//
// The F-01 doc's data and analysis contract (canonical addition 2026-10-13):
// "Analys ska versionssättas och redovisa den nya frågan endast för patienter
// som fått den aktuella eller senare formulärversionen. … Ingen äldre patient
// ska räknas som NOT_SURE_YET, inget svar eller avhopp på grund av att frågan
// saknades i hennes version." And: "Funnelanalys kan skilja formulärversion
// före och efter införandet."
//
// VERSION SPLIT (this cohort report, `f01Versions`). The cohort's people
// (same start, window and journey filter) by the F-01 version of their
// newest answer set: v10 or earlier (never asked), v11 or later, or no F-01
// yet. Each bucket goes through the cohort's own steps, so the introduction
// itself can be measured. Drop-off is per step, never per missing answer.
// No answer content is read for it.
//
// DESIRED SUPPORT (desiredSupportAnalysisSchema). Per-answer counts are F-01
// health data, and Ö-535 (Emelie PUA, 2026-10-13) puts them in the separate
// aggregated insight view, not in this funnel: "i admin, i en egen aggregerad
// kvalitets- och produktinsiktsvy … TES-1323 blir en panel" (TES-1326, gated
// by F01_INSIGHTS_MODE and audited there). The schema is that panel's
// payload. Only the v11+ people. Per code: how many chose it, and how many of
// them reached each later step by the time the report is read. Per pair (max
// two choices): how many chose exactly that pair.
//
// SMALL GROUPS. "Färre än 10 personer: visa inte värdet; visa ”Färre än
// 10”." Every count below DESIRED_SUPPORT_MIN_GROUP is null, so the value
// never leaves the API. Counts only: no ids, no free text (the "Annat" text
// is never read), nothing per person.
export const F01_VERSION_BUCKETS = [
  "before_desired_support",
  "with_desired_support",
  "no_f01",
] as const;
export type F01VersionBucket = (typeof F01_VERSION_BUCKETS)[number];

export const DESIRED_SUPPORT_ANALYSIS_STEPS = [
  // TES-1167's "Personlig återkoppling visad" (web beacon or app event).
  "feedback_visible",
  // The journey funnel's "Vårdmöte bokat" (paid before it is booked). Only
  // the new-treatment journey books one, so only its people are eligible.
  "meeting_booked",
  // The journey funnel's last step: prescription issued, treatment started.
  "prescription",
] as const;
export type DesiredSupportAnalysisStep =
  (typeof DESIRED_SUPPORT_ANALYSIS_STEPS)[number];

export const DESIRED_SUPPORT_MIN_GROUP = 10;

/** A count, or null when it is below DESIRED_SUPPORT_MIN_GROUP. */
const suppressed = count.nullable();

export const desiredSupportAnalysisSchema = z.object({
  minGroup: z.number().int().positive(),
  /** The v11+ people in the cohort. */
  population: suppressed,
  codes: z.array(
    z.object({
      code: z.string(),
      count: suppressed,
      steps: z.array(
        z.object({
          key: z.enum(DESIRED_SUPPORT_ANALYSIS_STEPS),
          /** Who chose the code and could reach the step. */
          eligible: suppressed,
          reached: suppressed,
        }),
      ),
    }),
  ),
  /**
   * Exactly two choices, in the quiz's option order. Only pairs chosen by at
   * least `minGroup` people are listed at all.
   */
  pairs: z.array(z.object({ codes: z.tuple([z.string(), z.string()]), count })),
});
export type DesiredSupportAnalysis = z.infer<
  typeof desiredSupportAnalysisSchema
>;

export const onboardingCohortReportSchema = z.object({
  /** Inclusive Europe/Stockholm days of the START, YYYY-MM-DD. */
  from: z.string(),
  to: z.string(),
  timeZone: z.literal("Europe/Stockholm"),
  windowDays: z.number().int().positive(),
  /** False until every member's window has closed. */
  complete: z.boolean(),
  /** When the last possible member's window closes (ISO instant). */
  windowsCloseAt: z.string(),
  channels: z.array(cohortGroup.extend({ channel: z.enum(COHORT_CHANNELS) })),
  /** Only groups with at least one person. */
  breakdown: z.array(
    cohortGroup.extend({
      channel: z.enum(COHORT_CHANNELS),
      dimension: z.enum(COHORT_DIMENSIONS),
      key: z.string(),
    }),
  ),
  /** Web leads created in the period that no patient was ever linked to. */
  unlinkedLeads: count,
  /**
   * TES-1105 — app installs first opened in the period that nobody has
   * signed in on: anonymous, so not in the cohort. Optional for an older API.
   */
  unlinkedFirstOpens: count.optional(),
  /** BankID by person, with attempts as diagnostics only. */
  bankId: bankIdPersonsSchema,
  measuredSince: z.array(measurementStartSchema),
  /**
   * TES-1117 S3 — the journey filter applied (Alla = null). With a journey,
   * `channels` and `breakdown` count that journey's people only; the BankID
   * attempts and the unlinked leads/installs carry no journey and are
   * unchanged. Optional for an older API.
   */
  journey: patientGroupSchema.nullable().optional(),
  /** TES-1117 S3 — present with a journey filter; see journeyFunnelSchema. */
  journeyFunnel: journeyFunnelSchema.optional(),
  /**
   * TES-1167 — the six steps per origin segment: the four primary segments
   * always, the unknown buckets only when someone is in them. Optional for
   * an older API.
   */
  segments: z.array(segmentFunnelSchema).optional(),
  /** TES-1167 — how this cohort's origins were decided, per source. */
  originSources: z
    .array(z.object({ source: z.enum(ORIGIN_SOURCES), count }))
    .optional(),
  /**
   * TES-1323 — the cohort's steps per F-01 version bucket. Optional for an
   * older API.
   */
  f01Versions: z
    .array(cohortGroup.extend({ bucket: z.enum(F01_VERSION_BUCKETS) }))
    .optional(),
});
export type OnboardingCohortReport = z.infer<
  typeof onboardingCohortReportSchema
>;

// ── TES-1109: the contact list ("Kontaktlista") ───────────────────────────
//
// Admin only, and unlike the funnel and the cohort it names people: each row
// is a patient with a VERIFIED e-mail address, so the API logs the list as a
// read of every patient it returns (basis `support`, purpose administration,
// like TES-1102's booking diagnostics). No medical data: a row says how far
// she got in onboarding, never what she answered.
//
// WHO IS IN IT. Every real, not erased patient with a verified e-mail whose
// start (the cohort's definition: the earlier of her first linked lead and
// her patient row) falls in the period.
//
// ORIGINAL CHANNEL. The cohort's start channel, with the web split by the
// linked lead's UTM tags: `ig` as in the cohort's entry source, `email` for
// a utm_source or utm_medium naming e-mail or a newsletter, else `web`.
//
// CONSENT. Her latest product-feedback choice (TES-1106): `granted`,
// `withdrawn` (her latest choice is no), or `none` (no choice recorded). An
// e-mail address is never consent: someone registered before TES-1106 is
// `none`.
export const CONTACT_LIST_CHANNELS = ["web", "app", "ig", "email"] as const;
export type ContactListChannel = (typeof CONTACT_LIST_CHANNELS)[number];

export const CONTACT_LIST_CONSENTS = ["granted", "withdrawn", "none"] as const;
export type ContactListConsent = (typeof CONTACT_LIST_CONSENTS)[number];

export const onboardingContactRowSchema = z.object({
  patientId: z.string(),
  /** Decrypted name, or "Namn saknas". */
  patientName: z.string(),
  /** The decrypted address. */
  email: z.string(),
  emailVerified: z.boolean(),
  /** When the address was verified (ISO), or null. */
  emailVerifiedAt: z.string().nullable(),
  /** Her start (ISO). */
  startedAt: z.string(),
  channel: z.enum(CONTACT_LIST_CHANNELS),
  /** The furthest cohort step she has reached, with no window. */
  lastStep: z.enum(COHORT_STEPS),
  /** When she first reached `lastStep` (ISO). */
  lastStepAt: z.string(),
  /** Her latest recorded activity of any kind (ISO). */
  lastActivityAt: z.string(),
  feedbackConsent: z.enum(CONTACT_LIST_CONSENTS),
  /** When her latest consent choice was made (ISO), or null for `none`. */
  feedbackConsentAt: z.string().nullable(),
});
export type OnboardingContactRow = z.infer<typeof onboardingContactRowSchema>;

export const onboardingContactListSchema = z.object({
  /** Inclusive Europe/Stockholm days of the START, YYYY-MM-DD. */
  from: z.string(),
  to: z.string(),
  /** The filters the API applied; null = all. */
  channel: z.enum(CONTACT_LIST_CHANNELS).nullable(),
  consent: z.enum(CONTACT_LIST_CONSENTS).nullable(),
  /** Latest activity first. */
  rows: z.array(onboardingContactRowSchema),
});
export type OnboardingContactList = z.infer<typeof onboardingContactListSchema>;
